"use client";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./LiveLecture1050.css";

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isValid,
  parse,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { type FC, useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";

import { getEventOptions, getEvents } from "@/api/liveLecture";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/Select";
import { Button } from "@/components/atoms/Button";
import { cn, formatSectionTitleWithColor } from "@/lib/utils";
import type {
  Event,
  EventDetailsResponse,
  EventFormOptions,
  Filters,
  LiveLectureProps,
} from "@/types/liveLecture";

const CALENDAR_VIEWS = {
  MONTH: "month",
  DAY: "day",
};

const NAVIGATE = {
  NEXT: "NEXT",
  PREV: "PREV",
};

/**
 * BDU reference palette (bduedu.in/live-session). These back the CMS colour
 * fields so an unconfigured section already renders like the BDU portal.
 * v1 tokens: `bg-gray-800` events, `hover:bg-gray-600`, `bg-red-800` tooltip,
 * `text-gray-700` toolbar label, `bg-[#f3f4f6]` / `bg-[#b8b8b8]` view buttons.
 */
const BDU = {
  BACKGROUND: "#FFFFFF",
  TEXT: "#171719",
  DAY_SECTION: "#FFFFFF",
  DAY_TEXT: "#374151",
  VIEW_BUTTON_BG: "#F3F4F6",
  VIEW_BUTTON_ACTIVE_BG: "#B8B8B8",
  NAVIGATE: "#171719",
  BUTTON_TEXT: "#FFFFFF",
  EVENT: "#1F2937",
  EVENT_TEXT: "#FFFFFF",
  TOOLTIP: "#991B1B",
  TOOLTIP_TEXT: "#FFFFFF",
};

/**
 * Hardcoded in the reference component, so not admin-driven.
 * The Submit button is v1's `bg-button-gradient-red` — a gradient, not a flat
 * colour, so it can only come from `backgroundImage`. `button_bg_color` stays
 * the escape hatch: set it and the button falls back to a flat fill.
 */
const BUTTON_GRADIENT = "linear-gradient(to right, #FF0013, #990013)";
/** v1 shadcn `border-slate-200` on the filter triggers. */
const SELECT_BORDER = "#E2E8F0";
/** v1 `sm:border border-gray-400` around the calendar. */
const CALENDAR_BORDER = "#9CA3AF";
/** v1 `hover:!bg-gray-600` on an event tile. */
const EVENT_HOVER = "#4B5563";
const TOOLBAR_BUTTON_BORDER = "#b8b8b8";
const TOOLBAR_BUTTON_BG = "#f3f4f6";

/**
 * BDU v1 runs Tailwind's default scale, so every `sm:` in the reference means
 * 640px. This webapp remaps `sm` to 1080px, so the reference's breakpoint is
 * carried over as an explicit "min-[640px]:" variant. Written out literally
 * everywhere below — Tailwind v4 extracts classes by scanning source text, so a
 * composed `${BP}:w-full` would never generate the utility.
 */

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const LiveLecture1050: FC<LiveLectureProps> = ({
  data,
  handleTooltipContent: externalHandleTooltipContent,
  handleSelectedEventId: externalHandleSelectedEventId,
  tooltipContent: externalTooltipContent,
  selectedEventId: externalSelectedEventId,
}) => {
  const stylingData = data?.data ?? data ?? {};
  const {
    title,
    title_text_color,
    title_highlight_color,
    component_background_color,
    day_section_color,
    day_text_color,
    view_button_text_color,
    view_button_bg_color,
    view_button_active_bg_color,
    view_button_active_text_color,
    navigate_button_color,
    left_navigate_icon,
    right_navigate_icon,
    button_icon,
    button_text,
    button_text_color,
    button_bg_color,
    event_color,
    event_text_color,
    tooltip_color,
    tooltip_text_color,
    description,
    content,
  } = stylingData as NonNullable<NonNullable<LiveLectureProps["data"]>["data"]>;

  const [filters, setFilters] = useState<Filters>({
    courseName: "",
    semYear: "",
    batch: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    courseName: "",
    semYear: "",
    batch: "",
  });
  const [view, setView] = useState<string>(CALENDAR_VIEWS.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [eventFormOptions, setEventFormOptions] = useState<EventFormOptions>(
    {} as EventFormOptions
  );
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // The reference component is always driven by a parent that owns the tooltip.
  // Rendered as a CMS section there is no such parent, so fall back to local
  // state and only defer to the props when they are actually supplied.
  const [internalTooltipContent, setInternalTooltipContent] = useState<
    EventDetailsResponse | Record<string, unknown>
  >({});
  const [internalSelectedEventId, setInternalSelectedEventId] = useState("");

  const tooltipContent = externalTooltipContent ?? internalTooltipContent;
  const selectedEventId = externalSelectedEventId ?? internalSelectedEventId;

  const handleTooltipContent = (
    nextContent: EventDetailsResponse | Record<string, unknown>
  ) => {
    if (externalHandleTooltipContent) {
      externalHandleTooltipContent(nextContent);
      return;
    }
    setInternalTooltipContent(nextContent);
  };

  const handleSelectedEventId = (id: string) => {
    if (externalHandleSelectedEventId) {
      externalHandleSelectedEventId(id);
      return;
    }
    setInternalSelectedEventId(id);
  };

  const getStartAndEndDates = (date: Date) => {
    const startDate = startOfMonth(date);
    const endDate = endOfMonth(date);
    const customStartDate = subDays(startDate, 15);
    const customEndDate = addDays(endDate, 15);
    const formattedStartDate = format(customStartDate, "yyyy-MM-dd");
    const formattedEndDate = format(customEndDate, "yyyy-MM-dd");
    return { formattedStartDate, formattedEndDate };
  };

  const fetchEventsForRange = async (startDate: string, endDate: string) => {
    try {
      const eventData = await getEvents(startDate, endDate);
      return Array.isArray(eventData) ? eventData : [];
    } catch {
      return [];
    }
  };

  const isObjectEmpty = (obj: Record<string, unknown>) =>
    Object.keys(obj).length === 0;

  const hideTooltip = () => {
    handleTooltipContent({});
    handleSelectedEventId("");
  };

  // Hide the tooltip on scroll, but only while one is actually showing.
  useEffect(() => {
    const handleScroll = () => {
      if (
        !isObjectEmpty(tooltipContent as Record<string, unknown>) ||
        selectedEventId
      ) {
        hideTooltip();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tooltipContent, selectedEventId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const eventOptions = await getEventOptions();
      setEventFormOptions(eventOptions || {});
      const today = new Date();
      const { formattedStartDate, formattedEndDate } =
        getStartAndEndDates(today);
      const eventData = await fetchEventsForRange(
        formattedStartDate,
        formattedEndDate
      );
      setEvents(eventData);
      setFilteredEvents(eventData);
    };
    fetchInitialData();
  }, []);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleSubmit = (allEvents: Event[] = [], isSubmitted = false) => {
    const eventsData = isSubmitted ? allEvents : events;
    const updatedFilters = isSubmitted ? appliedFilters : filters;
    if (!isSubmitted) setAppliedFilters(filters);

    const validFilterKeys = Object.keys(updatedFilters).filter(
      (key) =>
        updatedFilters[key as keyof Filters] !== "" &&
        updatedFilters[key as keyof Filters] !== "SELECT"
    ) as (keyof Filters)[];
    if (validFilterKeys.length === 0) {
      return setFilteredEvents(eventsData);
    }

    const filteredValues = eventsData.filter((event) => {
      const { eventDetails } = event;
      return validFilterKeys.every(
        (key) => eventDetails?.[key] === updatedFilters[key]
      );
    });

    setFilteredEvents(filteredValues);
  };

  const handleViewChange = async (newView: string) => {
    if (newView === CALENDAR_VIEWS.DAY) {
      const date = new Date(currentDate);
      setCurrentDate(startOfMonth(date));
    }
    setView(newView);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(currentDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    setEvents(eventData);
    return handleSubmit(eventData, true);
  };

  const getNavigateByDate = (
    currentView: string
  ): Record<string, (date: Date) => Date> => {
    const navigateByDay = {
      PREV: (date: Date) => addDays(date, -1),
      NEXT: (date: Date) => addDays(date, 1),
      TODAY: () => startOfToday(),
    };

    const navigateByMonth = {
      PREV: (date: Date) => subMonths(date, 1),
      NEXT: (date: Date) => addMonths(date, 1),
      TODAY: () => startOfToday(),
    };

    return currentView === CALENDAR_VIEWS.DAY ? navigateByDay : navigateByMonth;
  };

  const handleNavigate = async (action: string) => {
    const dateManipulation = getNavigateByDate(view);
    const newDate = dateManipulation[action]
      ? dateManipulation[action](currentDate)
      : currentDate;

    setCurrentDate(newDate);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(newDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    setEvents(eventData);
    return handleSubmit(eventData, true);
  };

  const handleEventHover = (
    _e: React.MouseEvent,
    event: Event = {} as Event
  ) => {
    if (isTouchDevice) return;
    if (event.eventDetails) {
      handleTooltipContent(event.eventDetails);
      return;
    }
    handleTooltipContent({});
  };

  const handleEventMove = (e: React.MouseEvent) => {
    if (isTouchDevice) return;
    const currentX = e.clientX;
    const currentY = e.clientY;
    if (currentX !== lastMousePosition.x && currentY !== lastMousePosition.y) {
      setTooltipPosition({ x: currentX, y: currentY });
      setLastMousePosition({ x: currentX, y: currentY });
    }
  };

  const handleEventLeave = () => {
    if (isTouchDevice) return;
    hideTooltip();
  };

  const handleTouchStart = (event: Event, e: React.TouchEvent) => {
    if (!isTouchDevice) setIsTouchDevice(true);

    const touchX = e.touches?.[0]?.clientX ?? 0;
    const touchY = e.touches?.[0]?.clientY ?? 0;
    const screenWidth = window.innerWidth;
    const isRightSide = touchX > screenWidth * 0.5;

    setTooltipPosition({
      x: isRightSide ? touchX - 250 : touchX + 10,
      y: touchY - 10,
    });

    if (event.eventDetails) {
      handleTooltipContent(event.eventDetails);
    }
  };

  const handleTouchEnd = () => {
    hideTooltip();
  };

  const handleCalendarMouseLeave = () => {
    hideTooltip();
  };

  const getFormattedTime = (time?: string) => {
    if (!time) return "N/A";
    const date = parse(time, "HH:mm:ss", new Date());
    return isValid(date) ? format(date, "hh:mma") : "N/A";
  };

  const getFormattedDate = (date?: string | Date) => {
    if (!date) return "N/A";
    const parsed = date instanceof Date ? date : new Date(date);
    return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "N/A";
  };

  const details = tooltipContent as Partial<EventDetailsResponse>;

  // v1's trigger resolves to shadcn's baseline plus its own sizing. This
  // webapp's Select atom has an EMPTY default baseline (`triggerDefault: ""`),
  // so the border/radius/shadow/background have to be spelled out here or the
  // filters render as unstyled text.
  const selectTriggerClassName = cn(
    "flex items-center justify-between whitespace-nowrap",
    "h-[40px] w-[327px] gap-[5px] rounded-md border px-3 py-2 shadow-sm",
    "bg-[#FFFFFF] text-[16px] font-normal normal-case leading-normal cursor-pointer",
    "min-[640px]:w-full"
  );

  // `contentDefault`/`itemDefault` are empty strings too, so the dropdown has no
  // background or padding of its own — it must be styled here or the options
  // render unreadably over the page. z-[120] clears the admin preview Dialog's
  // z-[110] overlay.
  const selectContentClassName = cn(
    "z-[120] w-[var(--radix-select-trigger-width)] overflow-hidden",
    "rounded-md border border-[#E2E8F0] bg-[#FFFFFF] shadow-lg"
  );

  const selectViewportClassName = "max-h-[300px] min-h-0 overflow-y-auto p-1";

  const selectItemClassName = cn(
    "relative cursor-pointer select-none rounded-sm px-3 py-2 text-[16px]",
    "text-[#171719] outline-none hover:bg-[#F1F5F9] focus:bg-[#F1F5F9]",
    "data-[state=checked]:font-medium"
  );

  const navButtonClassName = cn(
    "flex h-8 w-[29px] items-center justify-center border text-[22px]",
    "hover:bg-[#eaecf0] min-[640px]:w-14"
  );

  const viewButtonClassName = "h-8 w-14 border text-[14px]";

  return (
    <div
      className="w-full"
      onClick={handleTouchEnd}
      onMouseOver={!isTouchDevice ? handleEventLeave : undefined}
      style={{ backgroundColor: component_background_color || BDU.BACKGROUND }}
    >
      {/* v1 wraps the component in its own `mx-auto max-w-[1120px]` container.
          The CMS shell provides none, so the widget owns it. */}
      <div className="mx-auto w-full max-w-[1120px]">
        {title && (
          <h2
            className={cn(
              "col-span-2 mt-14 px-[14px] text-[20px] font-semibold leading-normal",
              "min-[640px]:pl-[31px] min-[640px]:text-[24px]"
            )}
            style={{ color: title_text_color || BDU.TEXT }}
            dangerouslySetInnerHTML={{
              __html: formatSectionTitleWithColor(
                title,
                title_highlight_color,
                title_text_color || BDU.TEXT
              ),
            }}
          />
        )}

        {(content || data?.content) && (
          <div
            dangerouslySetInnerHTML={{
              __html: content || data?.content || "",
            }}
            className={cn("col-span-2 mt-14 px-[14px]", "min-[640px]:pl-[31px]")}
          />
        )}

        <div
          className={cn(
            "mt-[25px] flex flex-col items-center justify-center gap-[25px]",
            "min-[640px]:max-w-[790px] min-[640px]:flex-row min-[640px]:justify-start min-[640px]:px-[31px]"
          )}
        >
          <Select
            onValueChange={(value: string) =>
              handleFilterChange("courseName", value)
            }
          >
            <SelectTrigger
              className={cn(selectTriggerClassName, "min-[640px]:shrink")}
              style={{ borderColor: SELECT_BORDER, color: BDU.TEXT }}
            >
              <SelectValue placeholder="Select Course Name" />
            </SelectTrigger>
            <SelectContent
              className={selectContentClassName}
              viewportClassName={selectViewportClassName}
            >
              <SelectGroup>
                <SelectItem value="SELECT" className={selectItemClassName}>
                  Select Course Name
                </SelectItem>
                {eventFormOptions?.courseName?.map((course: string) => (
                  <SelectItem
                    key={course}
                    value={course}
                    className={selectItemClassName}
                  >
                    {course}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select
            onValueChange={(value) => handleFilterChange("semYear", value)}
          >
            <SelectTrigger
              className={selectTriggerClassName}
              style={{ borderColor: SELECT_BORDER, color: BDU.TEXT }}
            >
              <SelectValue placeholder="Select Semester" />
            </SelectTrigger>
            <SelectContent
              className={selectContentClassName}
              viewportClassName={selectViewportClassName}
            >
              <SelectGroup>
                <SelectItem value="SELECT" className={selectItemClassName}>
                  Select Semester
                </SelectItem>
                {eventFormOptions?.semYear?.map((sem: string) => (
                  <SelectItem
                    key={sem}
                    value={sem}
                    className={selectItemClassName}
                  >
                    {sem}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select onValueChange={(value) => handleFilterChange("batch", value)}>
            <SelectTrigger
              className={selectTriggerClassName}
              style={{ borderColor: SELECT_BORDER, color: BDU.TEXT }}
            >
              <SelectValue placeholder="Select Batch" />
            </SelectTrigger>
            <SelectContent
              className={selectContentClassName}
              viewportClassName={selectViewportClassName}
            >
              <SelectGroup>
                <SelectItem value="SELECT" className={selectItemClassName}>
                  Select Batch
                </SelectItem>
                {eventFormOptions?.batch?.map((value: string) => (
                  <SelectItem
                    key={value}
                    value={value}
                    className={selectItemClassName}
                  >
                    {value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Button
            className={cn(
              "flex h-[40px] w-[327px] items-center justify-center gap-[7px]",
              "rounded-md px-5 py-2 text-[16px] font-normal normal-case leading-normal shadow",
              "transition-opacity active:opacity-70 min-[640px]:w-full"
            )}
            onClick={() => handleSubmit()}
            style={{
              backgroundImage: button_bg_color ? undefined : BUTTON_GRADIENT,
              backgroundColor: button_bg_color || undefined,
              color: button_text_color || BDU.BUTTON_TEXT,
            }}
          >
            {button_text || "Submit"}
            {button_icon && (
              <img src={button_icon} alt="" className="size-6" aria-hidden />
            )}
          </Button>
        </div>

        <div
          className={cn(
            "my-14 h-[640px] w-full pb-0",
            "min-[640px]:border min-[640px]:px-[21px]"
          )}
          style={{ borderColor: CALENDAR_BORDER }}
          onMouseLeave={handleCalendarMouseLeave}
          onClick={handleTouchEnd}
        >
          {!isObjectEmpty(tooltipContent as Record<string, unknown>) && (
            <div className="relative">
              <div
                className={cn(
                  selectedEventId ? "absolute w-[200px]" : "fixed w-[250px]",
                  "z-[45] rounded-md px-3 py-2 text-sm shadow-md"
                )}
                style={{
                  backgroundColor: tooltip_color || BDU.TOOLTIP,
                  color: tooltip_text_color || BDU.TOOLTIP_TEXT,
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  pointerEvents: "none",
                  transform: selectedEventId
                    ? "translate(-10%, -120%)"
                    : "translate(10%, -10%)",
                }}
              >
                <p>Course:- {details?.courseName}</p>
                <p>Subject:- {details?.subjectName}</p>
                <p>Date:- {getFormattedDate(details?.startDate)}</p>
                <p>
                  Time:- {getFormattedTime(details?.startTime)} to{" "}
                  {getFormattedTime(details?.endTime)}
                </p>
                <p>Batch:- {details?.batch}</p>
                <p>Semester:- {details?.semYear}</p>
              </div>
            </div>
          )}

          <Calendar
            localizer={localizer}
            events={filteredEvents}
            selectable={false}
            startAccessor="start"
            endAccessor="end"
            views={[CALENDAR_VIEWS.MONTH, CALENDAR_VIEWS.DAY]}
            view={view}
            onView={handleViewChange}
            date={currentDate}
            dayPropGetter={() => ({
              className: view === CALENDAR_VIEWS.MONTH ? "bg-transparent" : "",
            })}
            showAllEvents
            eventPropGetter={() => ({
              className: "text-[12px] overflow-hidden !p-0",
            })}
            components={{
              timeGutterHeader: () => (
                <div className="flex items-center">All-Day</div>
              ),
              header: ({ date, label }: { date: Date; label?: string }) => (
                <div
                  className="flex w-full items-center justify-center py-2 font-medium"
                  style={{
                    backgroundColor: day_section_color || BDU.DAY_SECTION,
                    color: day_text_color || BDU.DAY_TEXT,
                  }}
                >
                  {view === CALENDAR_VIEWS.DAY
                    ? format(date, "EEEE")
                    : label || format(date, "EEE")}
                </div>
              ),
              toolbar: ({ date }: { date: Date }) => (
                <div
                  className={cn(
                    "flex items-center justify-between px-0 py-4",
                    "min-[640px]:px-6"
                  )}
                  onMouseOver={(e) => e.stopPropagation()}
                  onMouseMove={(e) => {
                    e.stopPropagation();
                    if (
                      !isObjectEmpty(tooltipContent as Record<string, unknown>)
                    )
                      handleEventLeave();
                  }}
                >
                  <div className="flex">
                    <button
                      type="button"
                      className={cn(navButtonClassName, "rounded-l-md border-r-0")}
                      style={{
                        borderColor: TOOLBAR_BUTTON_BORDER,
                        backgroundColor: TOOLBAR_BUTTON_BG,
                        color: navigate_button_color || BDU.NAVIGATE,
                      }}
                      onClick={() => handleNavigate(NAVIGATE.PREV)}
                      aria-label="Previous"
                    >
                      {left_navigate_icon ? (
                        <img
                          src={left_navigate_icon}
                          alt=""
                          className="size-5"
                        />
                      ) : (
                        <span>&#60;</span>
                      )}
                    </button>

                    <button
                      type="button"
                      className={cn(navButtonClassName, "rounded-r-md")}
                      style={{
                        borderColor: TOOLBAR_BUTTON_BORDER,
                        backgroundColor: TOOLBAR_BUTTON_BG,
                        color: navigate_button_color || BDU.NAVIGATE,
                      }}
                      onClick={() => handleNavigate(NAVIGATE.NEXT)}
                      aria-label="Next"
                    >
                      {right_navigate_icon ? (
                        <img
                          src={right_navigate_icon}
                          alt=""
                          className="size-5"
                        />
                      ) : (
                        <span>&#62;</span>
                      )}
                    </button>
                  </div>

                  {/* v1 writes `text-l`, which is not a Tailwind class and so
                      resolves to the inherited 16px — matched explicitly. */}
                  <span
                    className={cn("text-[16px]", "min-[640px]:text-[25px]")}
                    style={{ color: day_text_color || BDU.DAY_TEXT }}
                  >
                    {view === CALENDAR_VIEWS.DAY
                      ? format(date, "MMMM dd, yyyy")
                      : format(date, "MMMM yyyy")}
                  </span>

                  <div className="flex">
                    <button
                      type="button"
                      className={cn(
                        viewButtonClassName,
                        "rounded-l-md border-r-0"
                      )}
                      onClick={() => handleViewChange(CALENDAR_VIEWS.MONTH)}
                      style={{
                        borderColor: TOOLBAR_BUTTON_BORDER,
                        backgroundColor:
                          view === CALENDAR_VIEWS.MONTH
                            ? view_button_active_bg_color ||
                              BDU.VIEW_BUTTON_ACTIVE_BG
                            : view_button_bg_color || BDU.VIEW_BUTTON_BG,
                        color:
                          view === CALENDAR_VIEWS.MONTH
                            ? view_button_active_text_color || BDU.TEXT
                            : view_button_text_color || BDU.TEXT,
                      }}
                    >
                      Month
                    </button>

                    <button
                      type="button"
                      className={cn(viewButtonClassName, "rounded-r-md")}
                      onClick={() => handleViewChange(CALENDAR_VIEWS.DAY)}
                      style={{
                        borderColor: TOOLBAR_BUTTON_BORDER,
                        backgroundColor:
                          view === CALENDAR_VIEWS.DAY
                            ? view_button_active_bg_color ||
                              BDU.VIEW_BUTTON_ACTIVE_BG
                            : view_button_bg_color || BDU.VIEW_BUTTON_BG,
                        color:
                          view === CALENDAR_VIEWS.DAY
                            ? view_button_active_text_color || BDU.TEXT
                            : view_button_text_color || BDU.TEXT,
                      }}
                    >
                      Day
                    </button>
                  </div>
                </div>
              ),
              event: ({ event }: { event: any }) => {
                const typedEvent = event as Event;
                const isStartValid =
                  typedEvent?.start && isValid(typedEvent.start);
                const formattedStart = isStartValid
                  ? format(typedEvent.start, "hh:mma")
                  : "";
                const eventTitle = typedEvent?.title || "";
                const stableEventId = typedEvent.eventDetails?.id || eventTitle;
                const isActive = Boolean(
                  stableEventId && selectedEventId === stableEventId
                );

                return (
                  <div
                    id={stableEventId}
                    onMouseOver={
                      !isTouchDevice
                        ? (e) => {
                            e.stopPropagation();
                            handleEventHover(e, typedEvent);
                          }
                        : undefined
                    }
                    onMouseMove={
                      !isTouchDevice ? (e) => handleEventMove(e) : undefined
                    }
                    onMouseLeave={!isTouchDevice ? handleEventLeave : undefined}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleTouchStart(typedEvent, e);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-full p-1 transition-colors"
                    style={{
                      backgroundColor: isActive
                        ? EVENT_HOVER
                        : event_color || BDU.EVENT,
                      color: event_text_color || BDU.EVENT_TEXT,
                    }}
                    onMouseEnter={
                      !isTouchDevice
                        ? (e) => {
                            e.currentTarget.style.backgroundColor = EVENT_HOVER;
                          }
                        : undefined
                    }
                    onMouseOut={
                      !isTouchDevice
                        ? (e) => {
                            e.currentTarget.style.backgroundColor = isActive
                              ? EVENT_HOVER
                              : event_color || BDU.EVENT;
                          }
                        : undefined
                    }
                  >
                    {`${formattedStart} ${eventTitle}`}
                  </div>
                );
              },
            }}
          />

          {/* v1 writes `sm:text[24px]` (missing hyphen), so the note renders at
              14px at every width on the live page — matched, not "fixed". */}
          {description ? (
            <div
              className="text-[14px] leading-normal"
              style={{ color: "#000000" }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LiveLecture1050;
